// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  FormControl,
  Dialog,
  DialogContent,
  Divider,
  FormLabel,
  Grid,
  Toolbar,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import { CustomAsyncSelect } from "../../../components/CustomFields/CustomSelect";
import MedicineGroupSave from "../MedicineGroup/MedicineGroupSave";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetMedicineGenericNameDetail,
  CreateMedicineGenericName,
  UpdateMedicineGenericName,
} from "../../../services/MedicineGenericNameService";
import { PreferencesContext } from "../../../PreferenceContext";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetMedicineGroupList } from "../../../services/MedicineGroupService";

// SCHEMA FOR FORM
interface formInitialValueTypes {
  medicineGenericId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  code: string;
  medicineGroupId: Selection | null;
  medicineGroupName: string;
  medicinegenericName: string;
  isLocked: boolean;
  isDeleted: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  medicineId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  medicineId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

export default function MedicineGenericNameSave(Props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name } = Props;

  // DEFAULT SCHEMA VALUE FOR FORM
  const initMedicineDiluentNameData: formInitialValueTypes = {
    medicineGenericId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    code: "",
    medicinegenericName: isForPopUp ? name?.toUpperCase() : "",
    isLocked: false,
    isDeleted: false,
    medicineGroupId: null,
    medicineGroupName: "",
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawMedicineGenericId = urlParams.get("q");
  let curMedicineGenericId = 0;
  if (
    curRawMedicineGenericId !== null &&
    curRawMedicineGenericId !== undefined
  ) {
    let decId = edService.decrypt(curRawMedicineGenericId);
    if (
      decId !== "" &&
      decId !== null &&
      decId !== undefined &&
      isForPopUp === false
    ) {
      curMedicineGenericId = +decId;
    }
  }
  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  const [param, setParam] = useState(parameters);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  // STORE
  const [medicineGroupColl, setMedicineGroupColl] = useState<any[]>([]);
  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    medicineGenericId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    code: Yup.string(),
    medicineGroupId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    medicineGroupName: Yup.string().nullable(),
    medicinegenericName: Yup.string().required(
      "Medicine Generic Name is required"
    ),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initMedicineDiluentNameData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET Medicine Diluent Name DETAIL
  const getMedicineGenericIdDetailById = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append("MedicineGenericId", curMedicineGenericId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetMedicineGenericNameDetail(params);
      item = res.data.data;
      methods.reset({
        ...initMedicineDiluentNameData,
        medicineGenericId: item?.medicineGenericId || 0,
        code: item?.code || "",
        medicineGroupId: item?.medicineGroupId
          ? {
              label: item?.medicineGroupName || "",
              value: item?.medicineGroupId || 0,
            }
          : null,
        medicineGroupName: item?.medicineGroupName || "",
        medicinegenericName: item?.medicinegenericName || "",
        isLocked: item?.isLocked || false,
      });
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE Medicine Generic Name
  const saveMedicineGenericName = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        medicineGenericId: value.medicineGenericId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        code: value.code?.trim(),
        medicineGroupId: value.medicineGroupId?.value || null,
        medicineGroupName: value.medicineGroupName?.trim() || null,
        medicinegenericName: value.medicinegenericName?.trim(),
        isLocked: value.isLocked,
        isDeleted: value.isDeleted,
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curMedicineGenericId > 0) {
        result = await UpdateMedicineGenericName(formData);
      } else {
        result = await CreateMedicineGenericName(formData);
      }
      if (result?.data) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result.data) {
            handleClosePopUp("MEDICINE_GENERIC_CREATE", result.data);
          }
        }
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveMedicineGenericName(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  const handleManagePopup = async (code, option) => {
    setCustomPopUp({ ...customPopUp, open: false });
    if (code === "MEDICINE_GROUP_CREATE") {
      methods.setValue(
        "medicineGroupId",
        {
          label: option?.medicineGroupLable,
          value: option?.medicineGroupId?.toString(),
        },
        {
          shouldValidate: true,
        }
      );
      methods.setValue("medicineGroupName", option?.medicineGroupLable, {
        shouldValidate: true,
      });

      setIsLoading(true);
      await fetchMedicineGroup("", true);
      setIsLoading(false);
    }
  };

  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomPopUp({ ...customPopUp, open: value, name: label, code: code });
  };

  // GET MEDICINE GROUP
  async function fetchMedicineGroup(searchValue: string, oninit: boolean) {
    try {
      let medicineGroupColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      params.append("mode", "DROPDOWN");
      const hsnSacData: any = await GetMedicineGroupList(params);
      if (hsnSacData?.data?.data?.medicineGroupColl) {
        medicineGroupColl = hsnSacData.data.data.medicineGroupColl;
        if (oninit) setMedicineGroupColl(medicineGroupColl);
      }
      return medicineGroupColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER MEDICINE GROUP
  async function filterMedicineGroup(inputValue: string) {
    let result = await fetchMedicineGroup(inputValue, false);
    return result;
  }

  // MEDICINE GROUP LOAD ON SEARCH
  const loadMedicineGroupList = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineGroup(inputValue).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchMedicineGroup("", true);
      if (curMedicineGenericId > 0) {
        await getMedicineGenericIdDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curMedicineGenericId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}>
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}>
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.code
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Code
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("code")}
                          error={!!methods.formState.errors.code}
                          className={`text-input ${methods.formState.errors.code ? "error" : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.code?.message}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    md={9}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  ></Grid> */}
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineGroupId
                            ? "validation-message"
                            : ""
                        }`}>
                        Medicine Group
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input">
                        <Controller
                          control={methods.control}
                          name="medicineGroupId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineGroupId"}
                              FieldName={"medicineGroupId"}
                              defaultOptions={medicineGroupColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadMedicineGroupList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e) {
                                  if (e.__isNew__ === true) {
                                    handleClickOpen(
                                      true,
                                      e.label,
                                      "MEDICINE_GROUP"
                                    );
                                  }
                                }
                                if (
                                  methods
                                    ?.getValues()
                                    ?.medicineGroupId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineGroupId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "medicineGroupName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineGroupId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicinegenericName
                            ? "validation-message"
                            : ""
                        }`}>
                        Medicine Generic Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input">
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          autoFocus
                          size="small"
                          {...methods.register("medicinegenericName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue(
                                "medicinegenericName",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              ); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.medicinegenericName}
                          className={`text-input ${
                            methods.formState.errors.medicinegenericName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.medicinegenericName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}>
                    {curMedicineGenericId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
          <Dialog
            className="dialogPage"
            open={customPopUp.open && customPopUp.code === "MEDICINE_GROUP"}
            onClose={(e, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
            fullScreen
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              style: {
                width: "600px",
                height: "350px",
                maxWidth: "none",
              },
            }}>
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  style={{ fontWeight: 600, marginLeft: 0 }}
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6">
                  Save Medicine Group
                </Typography>
              </Toolbar>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </AppBar>
            <DialogContent sx={{ padding: "12px 24px" }}>
              <MedicineGroupSave
                handleClosePopUp={(code, company) => {
                  handleManagePopup(code, company);
                }}
                isForPopUp={customPopUp.open}
                name={customPopUp.name}
              />
            </DialogContent>
            <Divider />
          </Dialog>
        </FormProvider>
      </Box>
    </Box>
  );
}
